import { Box, IconButton, Icon, Menu, MenuButton, MenuItem, MenuList, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMoreHorizontal } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  NegotiationRoundStatus,
  SourcingEventStatus,
  useCompleteSourcingEventMutation,
  useDeleteSourcingEventMutation,
  useGetEvaluationQuery,
  useListBidsForEventQuery,
  useReopenCompletedSourcingEventMutation,
  type SourcingEventDto,
} from "../../autogen/bff-api";
import { RejectedRankedEntriesNotice } from "./designContest/RejectedRankedEntriesNotice";
import { useJuryReportHasUnsavedChanges } from "./designContest/jury";
import { Explanation } from "../../common/support/Explanation/Explanation";
import { useApiError } from "../../common/errors/useApiError";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { urls } from "../../urls";
import { isDesignContest } from "../sourcing-events/buyer/wizard/utils/designContest";
import { isQualificationScheme } from "../sourcing-events/buyer/wizard/utils/procedure";
import { useCopyProcurement } from "../sourcing-events/useCopyProcurement";
import { EventChangeEditor } from "./changes/EventChangeEditor";
import { useChangeFlow } from "./changes/useChangeFlow";
import { everyScoreIn } from "./evaluation/evaluationCompleteness";
import { useCompleteEvaluationDescription } from "./evaluation/useCompleteEvaluationDescription";
import { useRoundSelection } from "./rounds/RoundSelectionContext";
import { useHasPassed } from "./bidDeadlineWindow";
import { publicBidDeadlineAhead, roundStillOpen } from "./rounds/roundStillOpen";
import { supportsNegotiationRounds } from "./rounds/supportsNegotiationRounds";
import { useStartRound } from "./rounds/useStartRound";
import { publishedBidDeadline } from "./qualification/stage";
import { MissingContractsNotice } from "./MissingContractsNotice";
import { EndQualificationSchemeModal } from "./result-notice/EndQualificationSchemeModal";
import { schemeEnding } from "./result-notice/schemeEnding";
import { useCancelOrCloseProcurement } from "./useCancelOrCloseProcurement";
import { useAwardMayBeWithdrawn, useCopyRefusal } from "./FrameworkNoLongerActive";

export const EventLifecycleMenu = ({ event }: { event: SourcingEventDto }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const navigate = useNavigate();
  const confirmationModal = useConfirmationModal();
  const completeEvaluationDescription = useCompleteEvaluationDescription(event);

  const [completeEvent] = useCompleteSourcingEventMutation();
  const [reopenEvent] = useReopenCompletedSourcingEventMutation();
  const [deleteEvent] = useDeleteSourcingEventMutation();
  const copyOperation = event.operations?.find((operation) => operation.operation === "CopySourcingEvent");
  const { copy: copyProcurement, isCopying, modal: copyModal } = useCopyProcurement();
  const copyRefusal = useCopyRefusal(event);
  const copyAllowed = !!copyOperation?.allowed && copyRefusal === null;
  const copyExplanation = copyRefusal ?? (copyOperation?.reason ? t(copyOperation.reason.code) : "");

  const cancelOrClose = useCancelOrCloseProcurement(event);
  const change = useChangeFlow(event);

  const status = event.status;
  const isDraft = status === SourcingEventStatus.Draft;
  const [endingScheme, setEndingScheme] = useState(false);
  const endsAsScheme =
    status === SourcingEventStatus.Published &&
    event.eventContext.type === "Public" &&
    isQualificationScheme(event.eventContext.procedureType);
  const endingPending =
    event.eventContext.type === "Public" && schemeEnding(event.eventContext.publications).state === "pending";
  const awardMayBeWithdrawn = useAwardMayBeWithdrawn(event);
  const canCancel = event.canCancel || awardMayBeWithdrawn;
  const canClose = !awardMayBeWithdrawn && event.canClose;
  const deadline = publishedBidDeadline(event);
  const deadlinePassed = deadline != null && new Date(deadline).getTime() <= Date.now();
  const canComplete =
    !awardMayBeWithdrawn &&
    ((status === SourcingEventStatus.Published && deadlinePassed) ||
      status === SourcingEventStatus.Evaluation ||
      status === SourcingEventStatus.Awarded);
  const awardStands = (event.award?.winningBidIds?.length ?? 0) > 0;
  const publicAwardStands = awardStands && event.eventContext.type === "Public";
  const awardCreatedAContract = (event.award?.contractIds?.length ?? 0) > 0;
  const canReopen =
    (status === SourcingEventStatus.Evaluated || status === SourcingEventStatus.Completed) && !publicAwardStands;

  // ADR-033 D1: round creation lives here now that negotiation is no longer a tab.
  const { rounds, latestSentRound, rejectedOrganizationIds, roundsLoaded } = useRoundSelection();
    const hasDraftRound = rounds.some((round) => round.status === NegotiationRoundStatus.Draft);
  const hasFinalRound = rounds.some((round) => round.isFinalRound === true);
  // A buyer who announced a forhandlingsforbehold may award on the initial tenders, but nothing
  // stops them negotiating anyway — the reservation is a right, not a commitment.
  const canCreateRound =
    supportsNegotiationRounds(event) &&
    (status === SourcingEventStatus.Published || status === SourcingEventStatus.Evaluation) &&
    !hasDraftRound &&
    !hasFinalRound &&
    roundsLoaded &&
    !publicBidDeadlineAhead(event) &&
    !rounds.some((round) => roundStillOpen(round, rejectedOrganizationIds));
  useHasPassed(
    [event.milestoneDates?.bidSubmissionDeadline, ...rounds.map((round) => round.deadline)]
      .filter((deadline): deadline is string => deadline != null && Date.parse(deadline) > Date.now())
      .sort()
      .at(-1),
  );

  const startRound = useStartRound(event);

  const completesEvaluation = status === SourcingEventStatus.Evaluation;
  const { data: evaluationForGate } = useGetEvaluationQuery(
    completesEvaluation ? { eventId: event.id, round: latestSentRound?.id } : skipToken,
  );
  const juryDecisionOperation = completesEvaluation
    ? event.operations?.find((operation) => operation.operation === "RegisterJuryDecision")
    : undefined;
  const juryRefusal = juryDecisionOperation && !juryDecisionOperation.allowed ? juryDecisionOperation.reason : null;
  const canCompleteEvaluation =
    !completesEvaluation || (juryDecisionOperation ? juryDecisionOperation.allowed : everyScoreIn(evaluationForGate));
  const juryReportUnsaved =
    useJuryReportHasUnsavedChanges(
      event.id,
      event.eventContext.type === "Public" ? event.eventContext.designContest : null,
    ) && completesEvaluation;
  const completionHeldBack = juryRefusal
    ? t(juryRefusal.code)
    : juryReportUnsaved
      ? t("The jury's report has unsaved changes. Save or discard them first.")
      : null;
  const reopensEvaluation = status === SourcingEventStatus.Evaluated;

  const completingAnAward = status === SourcingEventStatus.Awarded;
  const { isLoading: checkingContracts } = useListBidsForEventQuery(
    completingAnAward ? { eventId: event.id } : skipToken,
  );

  const completionDescription = () => {
    if (completesEvaluation) {
      return (
        <>
          <Text>{completeEvaluationDescription()}</Text>
          <RejectedRankedEntriesNotice event={event} />
        </>
      );
    }
    const completion = publicAwardStands
      ? t("The procurement is marked as completed. The award stands, so it cannot be reopened afterwards.")
      : t("The procurement is marked as completed. You can reopen it later.");
    if (!completingAnAward) return completion;
    return (
      <>
        <Text>{completion}</Text>
        <Box mt="3">
          <MissingContractsNotice event={event} />
        </Box>
      </>
    );
  };

  const reopenDescription = () => {
    const sentences = [t("The procurement is moved back to evaluation.")];
    if (!awardStands) return sentences.join(" ");

    sentences.push(
      t("The award is withdrawn and every bid goes back into the running. Suppliers who received an award letter are told."),
    );
    if (awardCreatedAContract) sentences.push(t("A contract that was created from the award is not removed."));
    return sentences.join(" ");
  };

  const designContest = isDesignContest(event.eventContext.type === "Public" ? event.eventContext.procedureType : null);
  const completeEvaluationTitle = designContest ? t("Register the jury's decision?") : t("Complete the evaluation?");
  const completeEvaluationLabel = designContest ? t("Register the jury's decision") : t("Complete the evaluation");

  const handleComplete = () => {
    confirmationModal.show({
      title: completesEvaluation ? completeEvaluationTitle : t("Complete the procurement?"),
      description: completionDescription(),
      onConfirmed: async () => {
        try {
          await completeEvent({ eventId: event.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const handleReopen = () => {
    confirmationModal.show({
      title: reopensEvaluation ? t("Reopen the evaluation?") : t("Reopen the procurement?"),
      description: reopenDescription(),
      onConfirmed: async () => {
        try {
          await reopenEvent({ eventId: event.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const handleCopy = () => copyProcurement(event.id);

  const handleDelete = () => {
    confirmationModal.show({
      title: t("Delete the draft?"),
      description: t("The draft is permanently removed."),
      confirmButtonColor: "red",
      confirmButtonText: t("Delete") ?? "",
      onConfirmed: async () => {
        try {
          await deleteEvent({ eventId: event.id }).unwrap();
          navigate(urls.app.sourcingEvents.viewAll);
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <>
      {confirmationModal.modal}
      {copyModal}
      {startRound.modal}
      {change.draft && change.editor.isOpen && (
        <EventChangeEditor
          key={change.draft.id}
          event={event}
          draft={change.draft}
          isOpen={change.editor.isOpen}
          onClose={change.editor.onClose}
        />
      )}
      {cancelOrClose.modal}

      <Menu>
        <MenuButton
          as={IconButton}
          size="sm"
          variant="outline"
          aria-label={t("Actions") ?? "Actions"}
          icon={<Icon as={FiMoreHorizontal} />}
        />
        <MenuList>
          {(change.canStartChange || change.draft) && (
            <MenuItem onClick={change.draft ? change.openChange : change.startChange} isDisabled={change.isStarting}>
              {change.actionLabel}
            </MenuItem>
          )}
          <Explanation text={copyExplanation} enabled={!copyAllowed}>
            <MenuItem onClick={handleCopy} isDisabled={isCopying || !copyAllowed} isFocusable={!copyAllowed}>
              {t("Copy the procurement")}
            </MenuItem>
          </Explanation>
          {canCreateRound && (
            <MenuItem onClick={startRound.start} isDisabled={startRound.isStarting}>
              {t("New round")}
            </MenuItem>
          )}
          {canComplete && (
            <MenuItem
              onClick={handleComplete}
              isDisabled={!canCompleteEvaluation || checkingContracts || juryReportUnsaved}
              isFocusable={completionHeldBack != null}
            >
              <Box>
                <Text>{completesEvaluation ? completeEvaluationLabel : t("Complete the procurement")}</Text>
                {completionHeldBack && (
                  <Text fontSize="xs" color="smMuted">
                    {completionHeldBack}
                  </Text>
                )}
              </Box>
            </MenuItem>
          )}
          {canReopen && (
            <MenuItem onClick={handleReopen}>
              {reopensEvaluation ? t("Reopen the evaluation") : t("Reopen the procurement")}
            </MenuItem>
          )}
          {endsAsScheme && (
            <Explanation text={t("The ending is sent to Doffin and waits to be published.")} enabled={endingPending}>
              <MenuItem
                color="smError"
                onClick={() => setEndingScheme(true)}
                isDisabled={endingPending}
                isFocusable={endingPending}
              >
                {t("End the qualification system")}
              </MenuItem>
            </Explanation>
          )}
          {canClose && !endsAsScheme && (
            <MenuItem onClick={() => cancelOrClose.open("close")}>{t("Close the procurement")}</MenuItem>
          )}
          {canCancel && !endsAsScheme && (
            <MenuItem color="smError" onClick={() => cancelOrClose.open("cancel")}>
              {t("Cancel the procurement")}
            </MenuItem>
          )}
          {isDraft && (
            <MenuItem color="smError" onClick={handleDelete}>
              {t("Delete the draft")}
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      {endsAsScheme && (
        <EndQualificationSchemeModal eventId={event.id} isOpen={endingScheme} onClose={() => setEndingScheme(false)} />
      )}
    </>
  );
};
